/**
 * SLA Calculator — calculates active processing time and SLA status.
 *
 * The SLA clock starts when a file is drawn (in_progress) and pauses
 * when the file is paused. Active time excludes all paused durations.
 * SLA breach occurs at 72 hours (3 active days).
 */

const SLA_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds

/**
 * Calculate the active processing time in milliseconds for a file.
 *
 * @param {object} file — file record with drawnAt, pausedAt, pausedTotalMs, status
 * @returns {number} active time in milliseconds
 */
export function calculateActiveTimeMs(file) {
  if (!file.drawnAt) return 0;

  const drawnAt = new Date(file.drawnAt).getTime();
  const now = Date.now();
  const totalElapsed = now - drawnAt;
  const pausedMs = file.pausedTotalMs || 0;

  // If currently paused, add the current pause duration
  let currentPauseMs = 0;
  if (file.status === 'paused' && file.pausedAt) {
    currentPauseMs = now - new Date(file.pausedAt).getTime();
  }

  return Math.max(0, totalElapsed - pausedMs - currentPauseMs);
}

/**
 * Calculate active processing time in days.
 */
export function calculateActiveDays(file) {
  const ms = calculateActiveTimeMs(file);
  return ms / (24 * 60 * 60 * 1000);
}

/**
 * Check whether a file has breached the SLA threshold.
 */
export function isSLABreached(file) {
  if (['sent_to_sign', 'signed', 'completed'].includes(file.status)) {
    // Check if it was breached at the time it reached sent_to_sign
    if (file.sentToSignAt && file.drawnAt) {
      const sentTime = new Date(file.sentToSignAt).getTime();
      const drawnTime = new Date(file.drawnAt).getTime();
      const activeAtSend = sentTime - drawnTime - (file.pausedTotalMs || 0);
      return activeAtSend > SLA_THRESHOLD_MS;
    }
    return file.slaBreached || false;
  }
  return calculateActiveTimeMs(file) > SLA_THRESHOLD_MS;
}

/**
 * Get the SLA urgency level for display purposes.
 * Returns: 'green' | 'amber' | 'red'
 */
export function getSLAUrgency(file) {
  if (!file.drawnAt) return 'green';

  const activeDays = calculateActiveDays(file);

  if (activeDays >= 3) return 'red';
  if (activeDays >= 2) return 'amber';
  return 'green';
}

/**
 * Get a human-readable SLA status string.
 */
export function getSLAStatusText(file) {
  const days = calculateActiveDays(file);

  if (days < 1) {
    const hours = Math.floor(days * 24);
    return `${hours}h active`;
  }
  return `${days.toFixed(1)} days active`;
}

/**
 * Get the SLA threshold in milliseconds (configurable in future).
 */
export function getSLAThresholdMs() {
  return SLA_THRESHOLD_MS;
}
